import { defineStore } from 'pinia'
import api from '@api/woocommerce'

export const useProductStore = defineStore('products', {
  state: () => ({
    allProducts: [],
    productCache: {},
    categories: [],
    attributes: [],
    currentProduct: null,
    loading: false,
    error: null,
    filters: {
      category: null,
      search: '',
      attributes: {}
    },
    // Client-side TTL - voorkom onnodige re-fetches bij navigatie
    _fetchedAllAt:        0,   // timestamp laatste fetchAllProducts
    _fetchedCategoriesAt: 0,   // timestamp laatste fetchCategories
  }),

  getters: {
    getProductById: (state) => (id) => {
      return state.productCache[id] ||
             state.allProducts.find(p => p.id === id)
    },

    getProductBySlug: (state) => (slug) => {
      return state.allProducts.find(p => p.slug === slug) ||
             Object.values(state.productCache).find(p => p.slug === slug)
    },

    hasActiveFilters: (state) => {
      return state.filters.category !== null ||
             state.filters.search !== '' ||
             Object.values(state.filters.attributes).some(val => val !== null)
    }
  },

  actions: {
    async fetchAllProducts(params = {}) {
      // Client-side cache: sla re-fetch over als data < 10 min oud is
      const age = Date.now() - this._fetchedAllAt
      if (this.allProducts.length > 0 && age < 10 * 60 * 1000) {
        return this.allProducts
      }

      this.loading = true
      this.error = null

      try {
        // Eén snelle call: de héle catalogus (kaart-shape, lichte velden) komt uit de
        // server-side in-memory pool — geen backend-round-trip, dus razendsnel én
        // volledig. Client-side filteren/sorteren/zoeken werkt hierop instant.
        const res = await fetch('/_products-all')
        const data = res.ok ? await res.json() : []
        this.allProducts = Array.isArray(data) ? data : []
        // Bewust NIET in productCache stoppen: dit is lichte kaart-data. De product-
        // pagina moet de volledige productdata ophalen (zie _lean-check hieronder).
        this._fetchedAllAt = Date.now()
        return this.allProducts
      } catch (error) {
        this.error = error?.message
        console.error('Error fetching products:', error)
        return this.allProducts
      } finally {
        this.loading = false
      }
    },

    async fetchProduct(id) {
      if (this.productCache[id] && !this.productCache[id]._lean) {
        this.currentProduct = this.productCache[id]
        return this.currentProduct
      }

      this.loading = true
      this.error = null

      try {
        const product = await api.getProduct(id)
        this.currentProduct = product
        this.productCache[product.id] = product
        return product
      } catch (error) {
        this.error = error.message
        console.error('Error fetching product:', error)
        return null
      } finally {
        this.loading = false
      }
    },

    async fetchProductBySlug(slug) {
      const cached = this.getProductBySlug(slug)
      if (cached && !cached._lean) {
        this.currentProduct = cached
        return cached
      }

      this.loading = true
      this.error = null

      try {
        const product = await api.getProductBySlug(slug)
        if (product) {
          this.currentProduct = product
          this.productCache[product.id] = product
        }
        return product
      } catch (error) {
        this.error = error.message
        console.error('Error fetching product by slug:', error)
        return null
      } finally {
        this.loading = false
      }
    },

    

    async fetchCategories() {
      // Client-side cache: categorieën veranderen zelden, 30 min TTL
      const age = Date.now() - this._fetchedCategoriesAt
      if (this.categories.length > 0 && age < 30 * 60 * 1000) {
        return this.categories
      }
      try {
        this.categories = await api.getCategories({ per_page: 100 })
        this._fetchedCategoriesAt = Date.now()
        return this.categories
      } catch (error) {
        console.error('Error fetching categories:', error)
        return []
      }
    },

    extractAttributesFromProducts() {
      const attributeMap = {}

      this.allProducts.forEach(product => {
        if (!product.attributes) return

        product.attributes.forEach(attr => {
          if (!attr.slug || !attr.options || !attr.visible) return

          if (!attributeMap[attr.slug]) {
            attributeMap[attr.slug] = {
              id: attr.id || attr.slug,
              name: attr.name,
              slug: attr.slug,
              termsSet: new Set()
            }
          }

          attr.options.forEach(option => {
            if (option && option.trim() !== '') {
              attributeMap[attr.slug].termsSet.add(option)
            }
          })
        })
      })

      this.attributes = Object.values(attributeMap)
        .map(attr => ({
          id: attr.id,
          name: attr.name,
          slug: attr.slug,
          terms: Array.from(attr.termsSet)
            .sort()
            .map((termName, index) => ({
              id: `${attr.slug}-${index}`,
              name: termName,
              slug: termName.toLowerCase().replace(/\s+/g, '-')
            }))
        }))
        .filter(attr => attr.terms.length > 0)

      return this.attributes
    },

    setFilter(key, value) {
      this.filters[key] = value
    },

    setAttributeFilter(slug, termName) {
      if (!this.filters.attributes) {
        this.filters.attributes = {}
      }
      this.filters.attributes[slug] = termName
    },

    clearAttributeFilter(slug) {
      if (this.filters.attributes) {
        delete this.filters.attributes[slug]
      }
    },

    clearAllAttributeFilters() {
      this.filters.attributes = {}
    },

    resetFilters() {
      this.filters = {
        category: null,
        search: '',
        attributes: {}
      }
    },

    clearCache() {
      this.productCache = {}
    }
  }
})